import React, { useMemo, useState } from 'react';
import { searchCategories } from '../../utils/categorySearch';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormHelperText,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Inventory2Outlined as ProductIcon,
  HandymanOutlined as ServiceIcon,
  Search as SearchIcon,
  StorefrontOutlined as StoreIcon,
  ImageOutlined as ImageIcon,
} from '@mui/icons-material';
import Avatar from '@mui/material/Avatar';
import { BUSINESS_TYPE_GROUPS } from '../../config/franchiseeBusinessTypes';
import { resolveCategoryThumbSrc } from '../../utils/locsySubcategoryThumbs';

const MAX_CATEGORIES = 8;

const resolveThumbUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/')) {
    const origin =
      typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}${url}`;
  }
  return url;
};

const CategoryThumb = ({ option, size = 28 }) => {
  const src =
    resolveCategoryThumbSrc({
      thumbnailUrl: option?.thumbnailUrl,
      categoryKey: option?.value,
      subCategoryId: option?.subCategoryId,
      mainCategoryId: option?.mainCategoryId,
      label: option?.label,
    }) || resolveThumbUrl(option?.thumbnailUrl);
  return (
    <Avatar
      variant="rounded"
      src={src || undefined}
      imgProps={{
        loading: 'lazy',
        decoding: 'async',
        onError: (e) => {
          e.currentTarget.style.display = 'none';
        },
      }}
      sx={{
        width: size,
        height: size,
        bgcolor: option?.group === 'products' ? '#e8f5e9' : '#e3f2fd',
        flexShrink: 0,
      }}
    >
      {option?.group === 'products' ? (
        <ProductIcon sx={{ fontSize: size * 0.55, color: '#2e7d32' }} />
      ) : src ? (
        <ImageIcon sx={{ fontSize: size * 0.55 }} color="disabled" />
      ) : (
        <ServiceIcon sx={{ fontSize: size * 0.55, color: '#1565c0' }} />
      )}
    </Avatar>
  );
};

const groupStyles = (group) =>
  group === 'products'
    ? { bg: '#e8f5e9', border: '#81c784', text: '#1b5e20', badge: 'Product' }
    : { bg: '#e3f2fd', border: '#64b5f6', text: '#0d47a1', badge: 'Service' };

const BusinessCategoriesPicker = ({
  categories = [],
  categoryOptions = [],
  onChange,
  onFeaturesSync,
  error,
  touched,
  maxCategories = MAX_CATEGORIES,
}) => {
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [pendingCategory, setPendingCategory] = useState(null);
  const [autocompleteKey, setAutocompleteKey] = useState(0);

  const optionByValue = useMemo(() => {
    const map = new Map();
    for (const opt of categoryOptions) {
      map.set(opt.value, opt);
    }
    return map;
  }, [categoryOptions]);

  const filterOptions = useMemo(
    () => [
      { value: 'all', label: 'All' },
      { value: 'services', label: BUSINESS_TYPE_GROUPS.services },
      { value: 'products', label: BUSINESS_TYPE_GROUPS.products },
    ],
    []
  );

  const searchableOptions = useMemo(() => {
    const selected = new Set(categories);
    let pool = categoryOptions.filter((item) => !selected.has(item.value));
    if (categoryFilter === 'services') {
      pool = pool.filter((item) => item.group === 'services');
    } else if (categoryFilter === 'products') {
      pool = pool.filter((item) => item.group === 'products');
    }
    return pool;
  }, [categoryFilter, categories, categoryOptions]);

  const canAdd =
    pendingCategory &&
    !categories.includes(pendingCategory.value) &&
    categories.length < maxCategories;

  const syncCategories = (nextCategories) => {
    onChange(nextCategories);
    if (onFeaturesSync) {
      onFeaturesSync(nextCategories);
    }
  };

  const handleAdd = () => {
    if (!canAdd) return;
    syncCategories([...categories, pendingCategory.value]);
    setPendingCategory(null);
    setAutocompleteKey((k) => k + 1);
  };

  const handleRemove = (value) => {
    syncCategories(categories.filter((item) => item !== value));
  };

  const showError = touched && Boolean(error);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: showError ? 'error.main' : 'divider',
        background: 'linear-gradient(180deg, #f8faff 0%, #ffffff 55%)',
        boxShadow: '0 8px 24px rgba(102, 126, 234, 0.08)',
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#667eea',
            color: '#fff',
            flexShrink: 0,
          }}
        >
          <StoreIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#1a237e', lineHeight: 1.3 }}>
            Business Categories
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
            Add each business separately — e.g. Kirana + Xerox + Flex Printing. Remove any one individually.
          </Typography>
        </Box>
      </Stack>

      <ToggleButtonGroup
        exclusive
        size="small"
        value={categoryFilter}
        onChange={(_, value) => {
          if (value) setCategoryFilter(value);
        }}
        sx={{
          mb: 2,
          flexWrap: 'wrap',
          gap: 0.5,
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            fontWeight: 600,
            px: 1.75,
            borderRadius: '999px !important',
            border: '1px solid #c5cae9 !important',
            '&.Mui-selected': {
              bgcolor: '#667eea',
              color: '#fff',
              borderColor: '#667eea !important',
            },
          },
        }}
      >
        {filterOptions.map((opt) => (
          <ToggleButton key={opt.value} value={opt.value}>
            {opt.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.25}
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        sx={{ mb: 2 }}
      >
        <Autocomplete
          key={`${categoryFilter}-${autocompleteKey}`}
          fullWidth
          autoHighlight
          options={searchableOptions}
          value={pendingCategory}
          onChange={(_, newValue) => setPendingCategory(newValue)}
          isOptionEqualToValue={(a, b) => a?.value === b?.value}
          getOptionLabel={(option) => option?.label || ''}
          filterOptions={(options, state) => {
            return searchCategories(options, state.inputValue);
          }}
          noOptionsText={
            categories.length >= maxCategories
              ? `Maximum ${maxCategories} categories added`
              : 'No match — try kirana, xerox, flex…'
          }
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.value}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                <CategoryThumb option={option} size={32} />
                <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                  {option.label}
                  {option.teluguName && <Typography component="span" variant="caption" color="text.secondary"> · {option.teluguName}</Typography>}
                </Typography>
                <Chip
                  size="small"
                  label={option.group === 'products' ? 'Product' : 'Service'}
                  sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700 }}
                  color={option.group === 'products' ? 'success' : 'primary'}
                  variant="outlined"
                />
              </Stack>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search category"
              placeholder="Type kirana, తాపీ, davakhana…"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <SearchIcon color="primary" fontSize="small" />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Button
          type="button"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canAdd}
          sx={{
            minWidth: { xs: '100%', sm: 148 },
            minHeight: 56,
            fontWeight: 700,
            bgcolor: '#667eea',
            '&:hover': { bgcolor: '#5a6fd6' },
            borderRadius: 2,
            flexShrink: 0,
          }}
        >
          Add
        </Button>
      </Stack>

      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderRadius: 2,
          bgcolor: categories.length ? '#f3f6ff' : '#fafafa',
          border: '1px dashed',
          borderColor: categories.length ? '#b39ddb' : '#e0e0e0',
          minHeight: 72,
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ color: '#5c6bc0', display: 'block', mb: 1.25, letterSpacing: 0.3 }}
        >
          SELECTED ({categories.length}/{maxCategories}) — tap ✕ on each to remove individually
        </Typography>
        {categories.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No categories yet. Search above and tap Add — you can mix products and services.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {categories.map((value, index) => {
              const meta = optionByValue.get(value);
              const group = meta?.group || 'services';
              const label = meta?.label || value;
              const styles = groupStyles(group);
              return (
                <Paper
                  key={`selected-${value}-${index}`}
                  variant="outlined"
                  sx={{
                    p: 1.25,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    bgcolor: styles.bg,
                    borderColor: styles.border,
                    borderRadius: 2,
                  }}
                >
                  <CategoryThumb option={meta || { group }} size={36} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ color: styles.text }}>
                      {label}
                      {index === 0 ? ' · Primary' : ''}
                    </Typography>
                    <Typography variant="caption" sx={{ color: styles.text, opacity: 0.85 }}>
                      {styles.badge}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    aria-label={`Remove ${label}`}
                    onClick={() => handleRemove(value)}
                    sx={{
                      bgcolor: '#fff',
                      border: '1px solid',
                      borderColor: styles.border,
                      color: styles.text,
                      flexShrink: 0,
                      '&:hover': { bgcolor: '#ffebee', color: '#c62828', borderColor: '#ef9a9a' },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Box>

      {showError && (
        <FormHelperText error sx={{ mt: 1, mx: 0, fontSize: '0.875rem' }}>
          {error}
        </FormHelperText>
      )}
      {!showError && (
        <FormHelperText sx={{ mt: 1, mx: 0, fontSize: '0.8rem' }}>
          First added category is primary. Each row has its own remove button.
        </FormHelperText>
      )}
    </Paper>
  );
};

export default BusinessCategoriesPicker;
